import React, { useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "@/api";
import { getToken, API } from "@/api";
import { PageHeader } from "@/pages/admin/AdminDashboard";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import { Plus, Edit3, Trash2, ClipboardCheck, Star, FileText, Award, ArrowRight, Send, Save, Download } from "lucide-react";

const emptyTemplate = () => ({ name: "", description: "", active: true, criteria: [{ key: "quality", label: "Qualité", max_score: 5, weight: 1 }] });
const emptyReport = () => ({ template_id: "", target_label: "", intervention_id: "", scores: [], global_comment: "", status: "draft" });

export default function ChefQualiParcAudits() {
  const [tab, setTab] = useState("reports");
  return (
    <div className="min-h-full">
      <PageHeader
        eyebrow="QualiParc"
        title="Audits & Rapports"
        sub="Créez des grilles d'audit personnalisées et suivez vos rapports notés par critère."
      />
      <div className="p-6 lg:p-10">
        <Tabs value={tab} onValueChange={setTab}>
          <TabsList data-testid="qp-audit-tabs">
            <TabsTrigger value="reports" data-testid="qp-audit-tab-reports">Rapports</TabsTrigger>
            <TabsTrigger value="templates" data-testid="qp-audit-tab-templates">Grilles / Templates</TabsTrigger>
          </TabsList>
          <TabsContent value="reports"><ReportsPane /></TabsContent>
          <TabsContent value="templates"><TemplatesPane /></TabsContent>
        </Tabs>
      </div>
    </div>
  );
}

function TemplatesPane() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyTemplate());

  const load = async () => {
    try { const { data } = await api.get("/chef/qualiparc/audit-templates"); setItems(data || []); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing(null); setForm(emptyTemplate()); setOpen(true); };
  const openEdit = (t) => {
    setEditing(t);
    setForm({ name: t.name, description: t.description || "", active: t.active !== false, criteria: t.criteria?.length ? t.criteria : [{ key: "quality", label: "Qualité", max_score: 5, weight: 1 }] });
    setOpen(true);
  };
  const addCriterion = () => setForm((f) => ({ ...f, criteria: [...f.criteria, { key: `crit_${f.criteria.length + 1}`, label: "", max_score: 5, weight: 1 }] }));
  const updateCriterion = (i, field, v) => setForm((f) => ({ ...f, criteria: f.criteria.map((c, idx) => (idx === i ? { ...c, [field]: field === "key" || field === "label" ? v : Number(v) } : c)) }));
  const removeCriterion = (i) => setForm((f) => ({ ...f, criteria: f.criteria.filter((_, idx) => idx !== i) }));

  const submit = async (e) => {
    e.preventDefault();
    const criteria = form.criteria.filter((c) => c.label.trim() && c.key.trim());
    if (!criteria.length) { toast.error("Ajoutez au moins un critère"); return; }
    try {
      const payload = { name: form.name.trim(), description: form.description, active: form.active, criteria };
      if (editing) await api.patch(`/chef/qualiparc/audit-templates/${editing.id}`, payload);
      else await api.post("/chef/qualiparc/audit-templates", payload);
      toast.success("Grille enregistrée");
      setOpen(false); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const remove = async (id) => {
    if (!window.confirm("Supprimer cette grille ?")) return;
    try { await api.delete(`/chef/qualiparc/audit-templates/${id}`); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div>
      <div className="flex justify-end mb-4">
        <Button onClick={openNew} data-testid="qp-audit-tpl-new" className="bg-[#002FA7]"><Plus className="h-4 w-4 mr-2" /> Nouvelle grille</Button>
      </div>
      {items.length === 0 ? (
        <div className="border border-[#DEE2E6] bg-white p-10 text-center text-sm text-[#868E96]" data-testid="qp-audit-tpl-empty">
          <ClipboardCheck className="h-8 w-8 mx-auto mb-2 opacity-40" />
          Aucune grille. Créez-en une pour piloter vos audits.
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {items.map((t) => (
            <div key={t.id} className="border border-[#DEE2E6] bg-white p-4" data-testid={`qp-audit-tpl-${t.id}`}>
              <div className="flex items-start justify-between gap-2">
                <div className="flex-1 min-w-0">
                  <h3 className="font-display font-bold">{t.name}</h3>
                  {t.description && <p className="text-xs text-[#495057] mt-1">{t.description}</p>}
                  <div className="text-[10px] uppercase tracking-[0.15em] text-[#868E96] mt-2">{t.criteria?.length || 0} critères</div>
                </div>
                <div className="flex gap-1">
                  <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => openEdit(t)}><Edit3 className="h-3.5 w-3.5" /></Button>
                  <Button size="icon" variant="ghost" className="h-8 w-8 text-red-600" onClick={() => remove(t.id)}><Trash2 className="h-3.5 w-3.5" /></Button>
                </div>
              </div>
              <div className="mt-3 flex flex-wrap gap-1">
                {(t.criteria || []).map((c) => (
                  <span key={c.key} className="text-[10px] px-2 py-0.5 border border-[#DEE2E6] bg-[#F8F9FA]">{c.label} · /{c.max_score}</span>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editing ? "Modifier la grille" : "Nouvelle grille"}</DialogTitle>
            <DialogDescription>Définissez les critères qui composent la note globale.</DialogDescription>
          </DialogHeader>
          <form onSubmit={submit} className="space-y-3">
            <div>
              <Label className="text-xs">Nom *</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required data-testid="qp-audit-tpl-name" />
            </div>
            <div>
              <Label className="text-xs">Description</Label>
              <Textarea rows={2} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
            </div>
            <div>
              <div className="flex items-center justify-between mb-2">
                <Label className="text-xs">Critères</Label>
                <Button type="button" size="sm" variant="outline" onClick={addCriterion} data-testid="qp-audit-tpl-add-crit"><Plus className="h-3 w-3 mr-1" /> Ajouter</Button>
              </div>
              <div className="space-y-2">
                {form.criteria.map((c, i) => (
                  <div key={i} className="grid grid-cols-12 gap-2 items-end">
                    <div className="col-span-3">
                      <Label className="text-[10px]">Clé</Label>
                      <Input value={c.key} onChange={(e) => updateCriterion(i, "key", e.target.value)} />
                    </div>
                    <div className="col-span-5">
                      <Label className="text-[10px]">Libellé</Label>
                      <Input value={c.label} onChange={(e) => updateCriterion(i, "label", e.target.value)} />
                    </div>
                    <div className="col-span-2">
                      <Label className="text-[10px]">Max</Label>
                      <Input type="number" min={1} value={c.max_score} onChange={(e) => updateCriterion(i, "max_score", e.target.value)} />
                    </div>
                    <div className="col-span-1">
                      <Label className="text-[10px]">Poids</Label>
                      <Input type="number" min={1} value={c.weight} onChange={(e) => updateCriterion(i, "weight", e.target.value)} />
                    </div>
                    <div className="col-span-1">
                      <Button type="button" size="icon" variant="ghost" className="h-8 w-8 text-red-600" onClick={() => removeCriterion(i)}><Trash2 className="h-3.5 w-3.5" /></Button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setOpen(false)}>Annuler</Button>
              <Button type="submit" className="bg-[#002FA7]" data-testid="qp-audit-tpl-save">{editing ? "Enregistrer" : "Créer"}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function ReportsPane() {
  const [items, setItems] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyReport());
  const [selectedTemplate, setSelectedTemplate] = useState(null);

  const load = async () => {
    try {
      const [a, b] = await Promise.all([
        api.get("/chef/qualiparc/audit-reports"),
        api.get("/chef/qualiparc/audit-templates"),
      ]);
      setItems(a.data || []);
      setTemplates((b.data || []).filter((t) => t.active !== false));
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  useEffect(() => { load(); }, []);

  const openNew = () => {
    setEditing(null);
    setForm(emptyReport());
    setSelectedTemplate(null);
    setOpen(true);
  };
  const openEdit = (r) => {
    setEditing(r);
    setSelectedTemplate(templates.find((t) => t.id === r.template_id) || null);
    setForm({
      template_id: r.template_id,
      target_label: r.target_label,
      intervention_id: r.intervention_id || "",
      scores: r.scores || [],
      global_comment: r.global_comment || "",
      status: r.status,
    });
    setOpen(true);
  };

  const onSelectTemplate = (tid) => {
    const t = templates.find((x) => x.id === tid);
    setSelectedTemplate(t || null);
    setForm((f) => ({
      ...f,
      template_id: tid,
      scores: (t?.criteria || []).map((c) => ({ criteria_key: c.key, criteria_label: c.label, score: 0, comment: "" })),
    }));
  };
  const updateScore = (i, field, v) => setForm((f) => ({ ...f, scores: f.scores.map((s, idx) => (idx === i ? { ...s, [field]: field === "score" ? Number(v) : v } : s)) }));

  const submit = async (status) => {
    try {
      const payload = { ...form, status };
      if (editing) {
        await api.patch(`/chef/qualiparc/audit-reports/${editing.id}`, payload);
      } else {
        if (!payload.template_id) { toast.error("Choisissez une grille"); return; }
        await api.post("/chef/qualiparc/audit-reports", payload);
      }
      toast.success(status === "submitted" ? "Rapport soumis" : "Brouillon enregistré");
      setOpen(false); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const remove = async (id) => {
    if (!window.confirm("Supprimer ce rapport ?")) return;
    try { await api.delete(`/chef/qualiparc/audit-reports/${id}`); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const downloadPdf = async (r) => {
    try {
      const res = await fetch(`${API}/chef/qualiparc/audit-reports/${r.id}/pdf`, {
        headers: { Authorization: `Bearer ${getToken()}` },
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `audit_${(r.target_label || "rapport").replace(/\s+/g, "_")}_${r.id.slice(0, 8)}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("PDF téléchargé");
    } catch (e) {
      toast.error("Erreur téléchargement PDF");
    }
  };

  return (
    <div>
      <div className="flex justify-end mb-4">
        <Button onClick={openNew} disabled={!templates.length} data-testid="qp-audit-rep-new" className="bg-[#002FA7]">
          <Plus className="h-4 w-4 mr-2" /> Nouveau rapport
        </Button>
      </div>
      {!templates.length && (
        <div className="border border-amber-200 bg-amber-50 p-3 text-xs text-amber-800 mb-4">
          Vous devez d'abord créer une grille dans l'onglet <b>Grilles / Templates</b>.
        </div>
      )}
      {items.length === 0 ? (
        <div className="border border-[#DEE2E6] bg-white p-10 text-center text-sm text-[#868E96]" data-testid="qp-audit-rep-empty">
          <FileText className="h-8 w-8 mx-auto mb-2 opacity-40" />
          Aucun rapport pour le moment.
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {items.map((r) => (
            <div key={r.id} className="border border-[#DEE2E6] bg-white p-4" data-testid={`qp-audit-rep-${r.id}`}>
              <div className="flex items-start justify-between gap-2">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className={`text-[10px] uppercase tracking-[0.15em] px-2 py-0.5 font-bold ${r.status === "submitted" ? "bg-emerald-50 text-emerald-700 border border-emerald-200" : "bg-amber-50 text-amber-700 border border-amber-200"}`}>
                      {r.status === "submitted" ? "Soumis" : "Brouillon"}
                    </span>
                    <span className="text-[10px] uppercase tracking-[0.15em] text-[#868E96]">{r.template_name}</span>
                  </div>
                  <h3 className="font-display font-bold text-base mt-1">{r.target_label}</h3>
                  <div className="text-xs text-[#495057] mt-1">Auditeur : {r.auditor_code}</div>
                </div>
                <div className="flex flex-col items-end gap-1">
                  <div className="flex items-center gap-1 font-display font-black text-2xl" style={{ color: r.global_score?.percent >= 75 ? "#059669" : r.global_score?.percent >= 50 ? "#D97706" : "#E60000" }}>
                    <Star className="h-4 w-4" /> {r.global_score?.percent ?? 0}%
                  </div>
                  <div className="text-[10px] text-[#868E96]">{r.global_score?.obtained}/{r.global_score?.max}</div>
                </div>
              </div>
              <div className="mt-3 flex gap-2">
                <Button size="sm" variant="outline" className="h-8 text-xs" onClick={() => openEdit(r)} data-testid={`qp-audit-rep-edit-${r.id}`}>
                  <Edit3 className="h-3 w-3 mr-1" /> Ouvrir
                </Button>
                <Button size="sm" variant="outline" className="h-8 text-xs" onClick={() => downloadPdf(r)} data-testid={`qp-audit-rep-pdf-${r.id}`}>
                  <Download className="h-3 w-3 mr-1" /> PDF
                </Button>
                <Button size="sm" variant="ghost" className="h-8 text-xs text-red-600" onClick={() => remove(r.id)}>
                  <Trash2 className="h-3 w-3 mr-1" /> Supprimer
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editing ? "Rapport d'audit" : "Nouveau rapport d'audit"}</DialogTitle>
            <DialogDescription>Notez chaque critère puis enregistrez ou soumettez.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs">Grille *</Label>
              <Select value={form.template_id} onValueChange={onSelectTemplate} disabled={!!editing}>
                <SelectTrigger data-testid="qp-audit-rep-tpl-select"><SelectValue placeholder="Sélectionner…" /></SelectTrigger>
                <SelectContent>
                  {templates.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Cible (site, personne, produit…) *</Label>
              <Input value={form.target_label} onChange={(e) => setForm({ ...form, target_label: e.target.value })} data-testid="qp-audit-rep-target" required />
            </div>
            {(selectedTemplate?.criteria || form.scores?.length > 0) && (
              <div className="space-y-2">
                <div className="text-[10px] uppercase tracking-[0.15em] text-[#868E96] font-semibold">Critères</div>
                {form.scores.map((s, i) => {
                  const crit = (selectedTemplate?.criteria || []).find((c) => c.key === s.criteria_key) || {};
                  const max = crit.max_score || 5;
                  return (
                    <div key={s.criteria_key} className="border border-[#DEE2E6] p-3 bg-white" data-testid={`qp-audit-score-${s.criteria_key}`}>
                      <div className="flex items-center justify-between mb-2">
                        <div className="text-sm font-semibold">{s.criteria_label || crit.label}</div>
                        <div className="flex items-center gap-1">
                          <Input type="number" min={0} max={max} className="w-16 h-8 text-center" value={s.score} onChange={(e) => updateScore(i, "score", e.target.value)} />
                          <span className="text-xs text-[#868E96]">/ {max}</span>
                        </div>
                      </div>
                      <Input placeholder="Commentaire (optionnel)" value={s.comment || ""} onChange={(e) => updateScore(i, "comment", e.target.value)} />
                    </div>
                  );
                })}
              </div>
            )}
            <div>
              <Label className="text-xs">Commentaire global</Label>
              <Textarea rows={3} value={form.global_comment} onChange={(e) => setForm({ ...form, global_comment: e.target.value })} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Annuler</Button>
            <Button variant="outline" onClick={() => submit("draft")} data-testid="qp-audit-rep-draft"><Save className="h-3.5 w-3.5 mr-1" /> Brouillon</Button>
            <Button onClick={() => submit("submitted")} data-testid="qp-audit-rep-submit" className="bg-[#059669] hover:bg-emerald-700"><Send className="h-3.5 w-3.5 mr-1" /> Soumettre</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
